import type { Middleware, Store } from '@reduxjs/toolkit';
import { configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query/react';
import type { Router } from '@remix-run/router';
import { ticCreateApiBase } from 'api/core/tic/tic.createApi.base';
import { timelyCreateApiBase } from 'api/core/timely/timely.createApi.base';
import { recurrenceBudgetApi } from 'api/recurrenceBudget/recurrenceBudget.api';
import { syncStatusApi } from 'api/syncStatus/syncStatus.api';
import deepFreeze from 'deep-freeze';
import { createMemoryRouter } from 'react-router-dom';
import type { ReducerWithMap } from 'utils/redux';
import { attachReducerInjector } from 'utils/redux';
import { createHistoryAdapter } from 'utils/routing';
import { dynamicMiddleware } from './middleware/dynamicMiddleware';
import { listenerMiddleware } from './middleware/listenerMiddleware';
import { createSentryMiddleware } from './middleware/sentryMiddleware';
import { syncRouterState } from './modules/router';
import type { IState } from './state';

export default function createStoreAndRouter<S extends object>(
  reducer: ReducerWithMap<S>,
  router: Router = createMemoryRouter([{ path: '/', element: null }])
) {
  const middleware: Array<Middleware> = [];

  if (__PRODUCTION__) {
    middleware.push(createSentryMiddleware());
  }
  // Uncomment to play around with Sentry in development
  // middleware.push(createSentryMiddleware())

  // Inject TIC's RTK Query API middleware
  middleware.push(ticCreateApiBase.middleware);
  middleware.push(timelyCreateApiBase.middleware);
  middleware.push(syncStatusApi.middleware);
  middleware.push(recurrenceBudgetApi.middleware);

  const finalCreateStore = configureStore({
    reducer,
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware({
        immutableCheck: false,
        serializableCheck: false,
        thunk: {
          extraArgument: createHistoryAdapter(router),
        },
      })
        .prepend(dynamicMiddleware.middleware, listenerMiddleware.middleware)
        .concat(...middleware, deepFreezeInDevelopment),
    devTools: process.env.NODE_ENV !== 'production' ? { trace: __TRACE__ } : false,
  });

  const store = attachReducerInjector(finalCreateStore, reducer.reducers);

  const mountStateToWindowObj = (store: Store) => {
    const updateWindowState = () => {
      (window as any).state = store.getState();
    };
    store.subscribe(updateWindowState);
    updateWindowState(); // Initial update
  };

  if (process.env.NODE_ENV !== 'production') {
    mountStateToWindowObj(store);
  }

  setupListeners(store.dispatch);

  router.subscribe(({ location, historyAction: action }) => {
    store.dispatch(syncRouterState({ location, action }));
  });

  return { store, router };
}

export type AppStore = ReturnType<typeof createStoreAndRouter<IState>>['store'];
export type AppDispatch = AppStore['dispatch'];

/** ImmutableCheck from redux-rtk is super slow. This is faster */
const deepFreezeInDevelopment: Middleware = api => {
  if (process.env.NODE_ENV === 'production') {
    return next => action => next(action);
  }
  deepFreeze(api.getState());
  return next => action => {
    deepFreeze(api.getState());
    try {
      return next(action);
    } finally {
      deepFreeze(api.getState());
    }
  };
};
